/**
 * Surgical role-elision for a serialized dockview layout (ADR-024 §6;
 * REQ-UI-047).
 *
 * # What this implements
 *
 * The "surgically elide role-restricted panels and reflow the grid" pass that
 * ADR-024 §6 calls for and that the workspace previously **deferred**. The old
 * gate (:func:`layoutIsRoleAccessible`) was all-or-nothing: a layout that
 * referenced ANY inaccessible panel was rejected wholesale and the workspace
 * fell back to the built-in default. That coarse gate forced an invariant on
 * :data:`DEFAULT_LAYOUT` — every panel in it had to be operator-accessible,
 * else a plain operator's *universal fallback* would itself be rejected.
 * Lifting that invariant is what lets the built-in default carry
 * role-restricted surfaces (System Status — support_engineer/admin; Audit Log
 * — admin) as tabs that simply elide for the roles that can't see them.
 *
 * # The transform
 *
 * Given a layout and the set of panel ids the current session may see
 * (:func:`getVisiblePanels`), returns a NEW layout with every inaccessible
 * panel removed:
 *
 *   - **Leaf (tab group):** ``views`` filtered to the allowed set; if the
 *     active tab was elided, ``activeView`` moves to the first surviving tab.
 *     A leaf whose every view was elided is dropped from its parent.
 *   - **Branch (split):** filtered child-by-child; a branch left with no
 *     children is dropped — so the grid reflows (dockview re-normalises the
 *     surviving siblings' proportional sizes at ``fromJSON`` time).
 *   - **panels map:** inaccessible ids removed.
 *   - **activeGroup:** preserved if its group survived, else re-pointed at the
 *     first surviving group (so the restored workspace always focuses a real
 *     group).
 *   - **floatingGroups / popoutGroups:** dropped. Meridian's UI exposes no
 *     float/popout affordance, so a captured layout never carries them; the
 *     defensive strip keeps a hypothetical future blob from dangling a
 *     reference past the panels filter (ADR-024 §6 — only grid + panels are the
 *     semantic contract).
 *
 * Returns ``null`` when NOTHING survives (no accessible panels) — the caller
 * treats that as "no usable layout" and falls through to its next source. In
 * practice every authenticated role sees ``map`` / ``platforms`` /
 * ``detections`` / ``alerts`` / ``entity-details`` (``PANEL_ROLES`` in
 * :file:`./registry-types.ts`), so the built-in default never fully elides.
 *
 * Pure + non-mutating: a fresh object graph is returned; the input is left
 * untouched so callers can keep the original around.
 *
 * Cross-references:
 *   - ADR-024 §6 (two-tier ownership + the elide-and-reflow restore contract).
 *   - REQ-UI-047 (role-restricted surfaces elided at restore) / REQ-UI-077
 *     (the built-in default Profile layout — DEFAULT_LAYOUT).
 *   - ./registry-types.ts (PANEL_ROLES / getVisiblePanels — the allowed set).
 *   - ./multi-pane-workspace.tsx (the three restore sites that call this).
 */
import type { SerializedDockview } from "dockview-react";
import type { PanelId } from "@/multi-pane/registry-types";

/**
 * A node in dockview's serialized grid tree (leaf or branch). Derived from
 * :type:`SerializedDockview` via indexed access to avoid importing dockview's
 * internal node-type names: a leaf's ``data`` is the group view-state, a
 * branch's ``data`` is an array of child nodes.
 */
type GridNode = SerializedDockview["grid"]["root"];
/** The group view-state carried by a leaf node (the non-array ``data`` arm). */
type LeafData = Extract<GridNode["data"], { views: string[] }>;

/**
 * Filter a single grid node, returning a new node — or ``null`` when the node
 * has no surviving content (an emptied leaf, or a branch all of whose children
 * emptied).
 */
function filterNode(node: GridNode, allowed: ReadonlySet<PanelId>): GridNode | null {
  if (Array.isArray(node.data)) {
    // Branch: filter children, drop the empties; an all-empty branch dies so
    // the parent reflows.
    const children: GridNode[] = [];
    for (const child of node.data) {
      const filtered = filterNode(child, allowed);
      if (filtered !== null) {
        children.push(filtered);
      }
    }
    if (children.length === 0) {
      return null;
    }
    return { ...node, data: children };
  }
  // Leaf: filter the tab views; an all-elided leaf dies.
  const leaf: LeafData = node.data;
  const views = leaf.views.filter((view) => allowed.has(view as PanelId));
  if (views.length === 0) {
    return null;
  }
  // Re-point the active tab if it was the one elided.
  const activeView =
    leaf.activeView !== undefined && views.includes(leaf.activeView) ? leaf.activeView : views[0];
  return { ...node, data: { ...leaf, views, activeView } };
}

/** Collect every surviving leaf's group ``id`` (for the activeGroup re-point). */
function collectLeafIds(node: GridNode, into: Set<string>): void {
  if (Array.isArray(node.data)) {
    for (const child of node.data) {
      collectLeafIds(child, into);
    }
    return;
  }
  into.add(node.data.id);
}

/**
 * Strip role-inaccessible panels from a serialized dockview layout. See the
 * module docstring for the full transform. Returns ``null`` when nothing
 * accessible survives (the caller falls through to its next layout source).
 *
 * @param layout - The serialized dockview state to filter (never mutated).
 * @param allowedPanels - The panel ids the current session may see
 *   (:func:`getVisiblePanels` of the session roles).
 */
export function filterLayoutByRole(
  layout: SerializedDockview,
  allowedPanels: ReadonlySet<PanelId>,
): SerializedDockview | null {
  // Filter the panels map down to the accessible ids.
  const panels: SerializedDockview["panels"] = {};
  for (const [id, entry] of Object.entries(layout.panels ?? {})) {
    if (allowedPanels.has(id as PanelId)) {
      panels[id] = entry;
    }
  }
  if (Object.keys(panels).length === 0) {
    return null;
  }

  const root = filterNode(layout.grid.root, allowedPanels);
  if (root === null) {
    return null;
  }

  // activeGroup: keep it if its group survived, else re-point at the first
  // surviving group so the restored workspace always focuses a real group.
  const survivingIds = new Set<string>();
  collectLeafIds(root, survivingIds);
  const activeGroup =
    layout.activeGroup !== undefined && survivingIds.has(layout.activeGroup)
      ? layout.activeGroup
      : survivingIds.values().next().value;

  // Reconstruct grid + panels only — floatingGroups / popoutGroups are dropped
  // (Meridian never creates them; defensive per the docstring).
  return {
    grid: { ...layout.grid, root },
    panels,
    ...(activeGroup !== undefined ? { activeGroup } : {}),
  };
}
